import { Component, Input, OnInit } from '@angular/core';
import { BatchTreeElement, EDocumentTypeNature, ResourceKey, TId } from 'src/app/models';
import { GenericReadOnlyResource, IGenericListOptions } from 'src/app/resources';
import { MasterApiService } from 'src/app/services/master-api.service';
import { firstValueFrom, Observable } from 'rxjs';
import { GenericCrudEditComponent } from 'src/app/libs/components/generic-crud/generic-crud-edit.component';
import { TabRole, TabsService } from 'src/app/libs/components/tabs-host/tabs.service';
import { ProductsBatchEditComponent } from '../../products-batch-edit.component';
import { ProductsEditComponent } from '../../../../products-operative-products/products-edit/products-edit.component';
import { OrganizationsEditComponent } from 'src/app/routes/directory/directory-stakeholders/organizations/organizations-edit/organizations-edit.component';
import { OmsPurchaseOrdersEditComponent } from 'src/app/routes/oms/logistics/oms-purchase-orders/oms-purchase-orders-edit/oms-purchase-orders-edit.component';
import { OmsSalesOrdersEditComponent } from 'src/app/routes/oms/logistics/oms-sales-orders/oms-sales-orders-edit/oms-sales-orders-edit.component';
import { OmsManufacturingOrdersEditComponent } from 'src/app/routes/oms/production/oms-manufacturing-orders/oms-manufacturing-orders-edit/oms-manufacturing-orders-edit.component';

export class BatchAncestorsDescendantsResource
  extends GenericReadOnlyResource<BatchTreeElement> {

  // treeOrder is not unique on its own (the same value repeats across depths),
  // so a composite key with depth is used to give every row a unique key and
  // avoid DevExtreme's E1040 duplicate-key error.
  override key = ['treeOrder', 'depth'] as unknown as ResourceKey<BatchTreeElement>

  constructor(
    protected override api: MasterApiService,
    private batchId: TId,
    private type: 'ancestors' | 'descendants'
  ) {
    super(api, 'ProductBatches');
  }

  override list(
    options: IGenericListOptions
  ): Observable<{ total: number; data: BatchTreeElement[] }> {
    options.pagination = options.pagination ?? { page: 1, size: 20 };

    if (this.type === 'descendants') {
      return this.api.getBatchListDescendants(this.batchId, options);
    }

    return this.api.getBatchListAncestors(this.batchId, options);
  }
}

@Component({
  selector: 'app-product-batch-ancestors-descendants',
  standalone: false,
  templateUrl: './product-batch-ancestors-descendants.component.html',
  styleUrl: './product-batch-ancestors-descendants.component.sass'
})
export class ProductBatchAncestorsDescendantsComponent implements OnInit {

  @Input() type: 'ancestors' | 'descendants' = 'ancestors';
  @Input() batchId?: TId;

  batchEditComponent = ProductsBatchEditComponent;
  productEditComponent = ProductsEditComponent;
  organizationEditComponent = OrganizationsEditComponent;
  resource?: BatchAncestorsDescendantsResource;

  constructor(private api: MasterApiService, private tabs: TabsService) {}

  ngOnInit(): void {
    if (!this.batchId) {
      this.resource = undefined;
      return;
    }

    this.resource = new BatchAncestorsDescendantsResource(
      this.api,
      this.batchId,
      this.type
    );
  }

  openProduct(element: BatchTreeElement) {
    this.tabs.createTab<GenericCrudEditComponent<any>>(
      this.productEditComponent!,
      TabRole.EDITION,
      { inputPrms: { id: element.productId } }
    );
  }

  openBatch(element: BatchTreeElement) {
    this.tabs.createTab<GenericCrudEditComponent<any>>(
      this.batchEditComponent!,
      TabRole.EDITION,
      { inputPrms: { id: element.productBatchId } }
    );
  }

  openOrganization(element: BatchTreeElement) {
    if (!element.organizationId) {
      return;
    }

    this.tabs.createTab<GenericCrudEditComponent<any>>(
      this.organizationEditComponent!,
      TabRole.EDITION,
      { inputPrms: { id: element.organizationId } }
    );
  }

  async openDocument(element: BatchTreeElement) {
    if (!element.documentId) {
      return;
    }

    // The tree endpoint only returns the document id/number, so the nature
    // (purchase order, sales order, production order...) that decides which
    // editor to open is resolved from the document's own document type.
    const document = await firstValueFrom(this.api.resources.operations.documents.get(element.documentId));
    const documentType = await firstValueFrom(this.api.resources.operations.documentTypes.get(document.documentTypeId));

    if (documentType.natureId === EDocumentTypeNature.PurchaseOrder) {
      this.tabs.createTab(OmsPurchaseOrdersEditComponent, TabRole.EDITION, { inputPrms: { id: element.documentId } });
      return;
    }
    if (documentType.natureId === EDocumentTypeNature.SalesOrder) {
      this.tabs.createTab(OmsSalesOrdersEditComponent, TabRole.EDITION, { inputPrms: { id: element.documentId } });
      return;
    }
    if (documentType.natureId === EDocumentTypeNature.ProductionOrder) {
      this.tabs.createTab(OmsManufacturingOrdersEditComponent, TabRole.EDITION, { inputPrms: { id: element.documentId } });
      return;
    }
  }
}
